// Static route-ribbon geometry (taxgw-2ybg): the render projection of one physical setup edge's authoritative
// SurfacePath, built once and shared by both orientations and every ownership sweep. A strip with two side vertices
// per tessellated centre point carries base positions, normalized side-offset directions, cumulative world distance
// and normalized progress; the vertex width stays a material uniform, and the fragment shader reveals a moving
// [tail, head] interval on the full fixed strip, so no corner is ever cut. The cache counts every edge against one
// setup-wide budget before allocating anything and disposes its geometries once (never per mesh).
//
// Geometry strategy (leg-by-leg forEachPathSample, every control point) belongs to content; this module never
// branches on the path kind.

import { BufferAttribute, BufferGeometry, Vector3 } from 'three'
import { forEachPathSample, type SurfacePath } from '@taxwar/content/geometry/routes'
import { InvariantViolation } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'
import { MAX_ROUTE_CENTRE_POINTS, ROUTE_STEP_RAD, countRouteSamples } from '../render/geo/routeTessellation'
import { ribbonSide } from './RibbonPool'

const XYZ = 3
const TRAIL_COMPONENTS = 2
const VERTICES_PER_POINT = 2
const INDICES_PER_SEGMENT = 6

/** Which way an oriented route reference reads its shared geometry: with the path (1) or against it (−1). */
export type RouteDirection = 1 | -1

/** One edge's route as one immutable ribbon strip, shared by both orientations and every play. */
export class RouteGeometry {
  readonly bufferGeometry: BufferGeometry
  /** World length of the route (render units), for dash distance and reverse orientation. */
  readonly lengthWorld: number

  /** Builds exactly the `centrePoints` samples `countRouteSamples` counted for `path`. */
  constructor(path: SurfacePath, centrePoints: number) {
    if (centrePoints < 2) throw new InvariantViolation(`route geometry needs at least two centre points, got ${centrePoints}`)
    const vertexCount = centrePoints * VERTICES_PER_POINT
    const positions = new Float32Array(vertexCount * XYZ)
    const sides = new Float32Array(vertexCount * XYZ)
    const trails = new Float32Array(vertexCount * TRAIL_COMPONENTS)
    const progress = new Float32Array(vertexCount)
    const indices = new Uint32Array((centrePoints - 1) * INDICES_PER_SEGMENT)

    const point = new Vector3()
    let built = 0
    let totalRad = 0
    forEachPathSample(path, ROUTE_STEP_RAD, (lat, lon, alongRad) => {
      latLonToVector3(lat, lon, GLOBE_ALTITUDE.network, point)
      const left = built * VERTICES_PER_POINT
      point.toArray(positions, left * XYZ)
      point.toArray(positions, (left + 1) * XYZ)
      const along = alongRad * GLOBE_ALTITUDE.network
      trails[left * TRAIL_COMPONENTS] = along
      trails[left * TRAIL_COMPONENTS + 1] = -1
      trails[(left + 1) * TRAIL_COMPONENTS] = along
      trails[(left + 1) * TRAIL_COMPONENTS + 1] = 1
      totalRad = alongRad
      built++
    })
    if (built !== centrePoints) throw new InvariantViolation(`route geometry sampled ${built} centre points, expected ${centrePoints}`)

    const lengthWorld = totalRad * GLOBE_ALTITUDE.network
    const here = new Vector3()
    const previous = new Vector3()
    const next = new Vector3()
    const tangent = new Vector3()
    const side = new Vector3()
    for (let i = 0; i < centrePoints; i++) {
      const left = i * VERTICES_PER_POINT
      const right = left + 1
      here.fromArray(positions, left * XYZ)
      previous.fromArray(positions, Math.max(0, i - 1) * VERTICES_PER_POINT * XYZ)
      next.fromArray(positions, Math.min(centrePoints - 1, i + 1) * VERTICES_PER_POINT * XYZ)
      tangent.subVectors(next, previous)
      ribbonSide(tangent, here, side)
      sides[left * XYZ] = -side.x
      sides[left * XYZ + 1] = -side.y
      sides[left * XYZ + 2] = -side.z
      sides[right * XYZ] = side.x
      sides[right * XYZ + 1] = side.y
      sides[right * XYZ + 2] = side.z
      const s = lengthWorld > 0 ? (trails[left * TRAIL_COMPONENTS] as number) / lengthWorld : 0
      progress[left] = s
      progress[right] = s
      if (i + 1 < centrePoints) {
        const o = i * INDICES_PER_SEGMENT
        indices[o] = left
        indices[o + 1] = right
        indices[o + 2] = left + VERTICES_PER_POINT
        indices[o + 3] = right
        indices[o + 4] = right + VERTICES_PER_POINT
        indices[o + 5] = left + VERTICES_PER_POINT
      }
    }

    this.lengthWorld = lengthWorld
    this.bufferGeometry = new BufferGeometry()
    this.bufferGeometry.setAttribute('position', new BufferAttribute(positions, XYZ))
    this.bufferGeometry.setAttribute('aSide', new BufferAttribute(sides, XYZ))
    this.bufferGeometry.setAttribute('aTrail', new BufferAttribute(trails, TRAIL_COMPONENTS))
    this.bufferGeometry.setAttribute('aProgress', new BufferAttribute(progress, 1))
    this.bufferGeometry.setIndex(new BufferAttribute(indices, 1))
  }

  dispose(): void {
    this.bufferGeometry.dispose()
  }
}

/** Every physical setup edge's RouteGeometry, built and disposed as one set (a match's route projections). */
export class RouteGeometryCache {
  private readonly geometries: readonly RouteGeometry[]

  constructor(paths: readonly SurfacePath[]) {
    // Budget first: count every edge against the setup-wide cap before any typed buffer is allocated.
    const counts = new Int32Array(paths.length)
    let remaining = MAX_ROUTE_CENTRE_POINTS
    for (let i = 0; i < paths.length; i++) {
      const count = countRouteSamples(paths[i] as SurfacePath, remaining)
      counts[i] = count
      remaining -= count
    }
    this.geometries = paths.map((path, i) => new RouteGeometry(path, counts[i] as number))
  }

  geometryOf(index: number): RouteGeometry {
    const geometry = this.geometries[index]
    if (geometry === undefined) throw new InvariantViolation(`route geometry ${index} is not on the war map`)
    return geometry
  }

  dispose(): void {
    for (const geometry of this.geometries) geometry.dispose()
  }
}
